// ProjectDetail — full DS reskin for /projects/:slug.
// Reads: GET /projects/:slug, GET /projects/:slug/ledger
// Writes: POST /settings/cost-entries/:id/reverse
import React, { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, Check, ExternalLink, ChevronRight, Pencil, Archive, ArchiveRestore, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { fmtUSD, fmtECU } from "../lib/format";
import {
  PageHeader, Panel, DataTable, Chip, DsButton,
} from "../ds";
import { Button } from "../components/ui/button";
import { EditProjectDialog, ProjectActionDialog } from "../components/EditProjectDialog";
import { ProjectSeoTab, ProjectMarketingTab } from "./project/ProjectSeoMarketingTabs";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

function fmtDubai(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat("en-GB", {
      timeZone: "Asia/Dubai",
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: false,
    }).format(d);
  } catch { return iso; }
}

const SOURCE_ORDER = ["emergent", "claude_api", "subscription", "other"];
const SOURCE_LABEL = {
  emergent: "Emergent",
  claude_api: "Claude API",
  subscription: "Subscription",
  other: "Other",
};

function CopyJobId({ id }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(id);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      onClick={copy}
      className="inline-flex items-center gap-1.5 font-mono text-[11px] text-[color:var(--muted)] hover:text-[color:var(--fg)]"
      data-testid={`copy-job-${id}`}
    >
      <span className="truncate max-w-[200px]">{id}</span>
      {copied ? <Check className="h-3 w-3" style={{ color: "var(--ok)" }} />
              : <Copy className="h-3 w-3" />}
    </button>
  );
}

function TotalsPanel({ label, totals, testId }) {
  const by = totals?.by_source || {};
  return (
    <Panel label={label} testId={testId}>
      <div className="flex items-baseline justify-between pb-2">
        <div className="flex items-baseline gap-5">
          <div>
            <div className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-[color:var(--muted)]">USD</div>
            <div className="font-mono text-[24px] text-[color:var(--fg)] tabular-nums" data-testid={`${testId}-usd`}>
              {fmtUSD(totals?.usd || 0)}
            </div>
          </div>
          <div>
            <div className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-[color:var(--muted)]">ECU</div>
            <div className="font-mono text-[24px] text-[color:var(--fg)] tabular-nums" data-testid={`${testId}-ecu`}>
              {fmtECU(totals?.ecu || 0)}
            </div>
          </div>
        </div>
      </div>
      <div className="mt-2 space-y-1 border-t border-[color:var(--line)] pt-2">
        {SOURCE_ORDER.map((src) => {
          const row = by[src];
          if (!row) return null;
          return (
            <div key={src} className="flex items-center justify-between text-xs font-mono tabular-nums"
                 data-testid={`${testId}-src-${src}`}>
              <span className="text-[color:var(--muted)]">{SOURCE_LABEL[src]}</span>
              <span className="text-[color:var(--fg)]">
                {fmtUSD(row.usd)}
                {row.ecu > 0 && <span className="text-[color:var(--muted)] ml-2">· {fmtECU(row.ecu)}</span>}
              </span>
            </div>
          );
        })}
        {Object.keys(by).length === 0 && (
          <div className="text-xs text-[color:var(--muted)]">No activity in this window.</div>
        )}
      </div>
    </Panel>
  );
}

function ReverseCell({ entry, onReversed }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  if (entry.is_reversal) {
    return <span className="font-mono text-[10.5px] uppercase text-[color:var(--muted)]">reversal</span>;
  }
  if (entry.reversed_by) {
    return (
      <Chip value="rejected" label="reversed" testId={`entry-${entry.id}-reversed`} />
    );
  }
  const doReverse = async () => {
    setBusy(true);
    try {
      await api.post(`/settings/cost-entries/${entry.id}/reverse`, { note: "" });
      toast.success("Entry reversed.");
      onReversed();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  };
  if (confirming) {
    return (
      <span className="inline-flex items-center gap-1.5" data-testid={`entry-${entry.id}-confirm-row`}>
        <DsButton variant="no" onClick={doReverse} disabled={busy} data-testid={`entry-${entry.id}-confirm`}>
          {busy ? "…" : "Confirm"}
        </DsButton>
        <DsButton variant="ghost" onClick={() => setConfirming(false)} data-testid={`entry-${entry.id}-cancel`}>
          Cancel
        </DsButton>
      </span>
    );
  }
  return (
    <DsButton onClick={() => setConfirming(true)} data-testid={`entry-${entry.id}-reverse`}>
      Reverse
    </DsButton>
  );
}

export default function ProjectDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [includeChildren, setIncludeChildren] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [actionOpen, setActionOpen] = useState(false);
  const [action, setAction] = useState("archive");

  const { data } = useQuery({
    queryKey: ["project", slug, includeChildren],
    queryFn: async () => (await api.get(`/projects/${slug}`,
      { params: { include_children: includeChildren } })).data,
  });
  const { data: ledgerData } = useQuery({
    queryKey: ["project-ledger", slug, includeChildren],
    queryFn: async () => (await api.get(`/projects/${slug}/ledger`,
      { params: { include_children: includeChildren, window: "lifetime", limit: 200 } })).data,
    enabled: !!data,
  });
  const { data: eligibility } = useQuery({
    queryKey: ["project-delete-eligibility", slug],
    queryFn: async () => (await api.get(`/projects/${slug}/delete-eligibility`)).data,
    enabled: !!data,
  });

  if (!data) {
    return (
      <div className="p-10 max-w-[1600px] mx-auto">
        <div className="text-sm text-[color:var(--muted)]">Loading…</div>
      </div>
    );
  }

  const { project, parent, children, tasks, totals_month, totals_lifetime } = data;
  const isParent = (children || []).length > 0;
  const entries = ledgerData?.entries || [];
  const isArchived = project.status === "archived";
  const canDelete = !!eligibility?.can_delete;
  const deleteReason = eligibility?.reason || "";

  const openAction = (a) => { setAction(a); setActionOpen(true); };

  const refetchAll = () => {
    qc.invalidateQueries({ queryKey: ["project", slug] });
    qc.invalidateQueries({ queryKey: ["project-ledger", slug] });
    qc.invalidateQueries({ queryKey: ["costs"] });
  };

  // Merge legacy single-field with new emergent_jobs array
  const linkedJobs = [
    ...(project.emergent_jobs || []),
    ...(project.emergent_job_id && !(project.emergent_jobs || []).some(j => j.job_id === project.emergent_job_id)
      ? [{ job_id: project.emergent_job_id, label: "primary", preview_url: project.preview_url }]
      : []),
  ];

  return (
    <div className="p-10 max-w-[1600px] mx-auto space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <PageHeader
            title={project.name}
            subtitle={project.description || ""}
          />
          {isArchived && (
            <span
              data-testid="project-archived-tag"
              className="inline-flex items-center gap-1.5 mt-2 rounded-full border border-amber-500/30 bg-amber-500/10 px-2.5 py-0.5 text-[11px] font-mono uppercase tracking-wider text-amber-500"
            >
              <Archive className="h-3 w-3" /> Archived
            </span>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => setEditOpen(true)} data-testid="edit-project-btn">
            <Pencil className="h-4 w-4 mr-2" /> Edit
          </Button>
          {isArchived ? (
            <Button variant="outline" onClick={() => openAction("restore")}
              className="bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30"
              data-testid="restore-project-btn">
              <ArchiveRestore className="h-4 w-4 mr-2" /> Restore
            </Button>
          ) : (
            <Button variant="outline" onClick={() => openAction("archive")}
              className="bg-amber-500/10 hover:bg-amber-500/20 text-amber-600 dark:text-amber-400 border border-amber-500/30"
              data-testid="archive-project-btn">
              <Archive className="h-4 w-4 mr-2" /> Archive
            </Button>
          )}
          <Button variant="outline"
            onClick={() => openAction("delete")}
            disabled={!canDelete}
            title={!canDelete ? deleteReason : "Permanently delete"}
            className="bg-rose-500/10 hover:bg-rose-500/20 text-rose-600 dark:text-rose-400 border border-rose-500/30 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="delete-project-btn">
            <Trash2 className="h-4 w-4 mr-2" /> Delete
          </Button>
        </div>
      </div>
      {!canDelete && deleteReason && (
        <p className="text-[11px] font-mono text-muted-foreground -mt-2" data-testid="delete-blocked-note">
          Delete disabled: {deleteReason}
        </p>
      )}

      {/* Breadcrumb for children */}
      <div className="flex items-center gap-2 flex-wrap -mt-2" data-testid="project-breadcrumb">
        <Link to="/projects"
              className="font-mono text-[11px] uppercase tracking-[0.08em] text-[color:var(--muted)] hover:text-[color:var(--fg)]">
          Projects
        </Link>
        <ChevronRight className="h-3 w-3 text-[color:var(--muted)]" />
        {parent && (
          <>
            <Link to={`/projects/${parent.slug}`}
                  className="font-mono text-[11px] uppercase tracking-[0.08em] text-[color:var(--muted)] hover:text-[color:var(--fg)]"
                  data-testid="parent-link">
              {parent.name}
            </Link>
            <ChevronRight className="h-3 w-3 text-[color:var(--muted)]" />
          </>
        )}
        <span className="font-mono text-[11px] uppercase tracking-[0.08em] text-[color:var(--fg)]">
          {project.slug}
        </span>
        {project.domain && (
          <>
            <span className="text-[color:var(--muted)]">·</span>
            <span className="font-mono text-[11px] text-[color:var(--cobalt)]">{project.domain}</span>
          </>
        )}
      </div>

      {/* Linked Emergent jobs */}
      <Panel label="LINKED EMERGENT JOBS" testId="project-jobs-panel">
        {linkedJobs.length === 0 ? (
          <div className="text-xs text-[color:var(--muted)]">No linked Emergent jobs.</div>
        ) : (
          <ul className="space-y-2" data-testid="emergent-jobs-list">
            {linkedJobs.map((j) => (
              <li key={j.job_id} className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3">
                  <span className="font-ui text-sm text-[color:var(--fg)]">{j.label || "job"}</span>
                  <CopyJobId id={j.job_id} />
                </div>
                {j.preview_url ? (
                  <a href={j.preview_url} target="_blank" rel="noopener noreferrer"
                     className="inline-flex items-center gap-1.5 font-mono text-[11px] text-[color:var(--cobalt)] hover:brightness-125"
                     data-testid={`open-preview-${j.job_id}`}>
                    Open preview <ExternalLink className="h-3 w-3" />
                  </a>
                ) : (
                  <span className="font-mono text-[10.5px] text-[color:var(--muted)]">no preview</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </Panel>

      {/* SEO + Marketing tabs — read-only snapshots pushed by VPS agents */}
      <Tabs defaultValue="seo" className="w-full">
        <TabsList data-testid="project-tabs-list">
          <TabsTrigger value="seo" data-testid="tab-seo">SEO</TabsTrigger>
          <TabsTrigger value="marketing" data-testid="tab-marketing">Marketing</TabsTrigger>
        </TabsList>
        <TabsContent value="seo" className="mt-4">
          <ProjectSeoTab slug={slug} />
        </TabsContent>
        <TabsContent value="marketing" className="mt-4">
          <ProjectMarketingTab slug={slug} />
        </TabsContent>
      </Tabs>

      {/* Totals */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <TotalsPanel label="LIFETIME SPEND" totals={totals_lifetime} testId="totals-lifetime" />
        <TotalsPanel label="THIS MONTH" totals={totals_month} testId="totals-month" />
      </div>
      {isParent && (
        <label className="inline-flex items-center gap-2 text-xs font-mono text-[color:var(--muted)] cursor-pointer"
               data-testid="include-children-toggle">
          <input
            type="checkbox"
            checked={includeChildren}
            onChange={(e) => setIncludeChildren(e.target.checked)}
            data-testid="include-children-checkbox"
          />
          Include children in totals
        </label>
      )}

      {/* Children */}
      {isParent && (
        <Panel label="SUB-PROJECTS" testId="children-panel">
          <DataTable
            testId="children-table"
            columns={[
              { key: "name", label: "Name",
                render: (c) => (
                  <Link to={`/projects/${c.slug}`}
                        className="text-[color:var(--fg)] hover:text-[color:var(--cobalt)]"
                        data-testid={`child-link-${c.slug}`}>
                    {c.name}
                  </Link>
                ) },
              { key: "lifetime", label: "Lifetime USD", right: true, width: 170,
                render: (c) => fmtUSD(c.totals_lifetime?.usd || 0) },
              { key: "month", label: "This month USD", right: true, width: 170,
                render: (c) => fmtUSD(c.totals_month?.usd || 0) },
            ]}
            rows={children.map((c) => ({ ...c, id: c.id }))}
            empty="No sub-projects."
          />
        </Panel>
      )}

      {/* Tasks */}
      <Panel label="TASKS" testId="project-tasks-panel">
        {tasks.length === 0 ? (
          <div className="text-xs text-[color:var(--muted)]">No tasks yet.</div>
        ) : (
          <DataTable
            testId="project-tasks-table"
            columns={[
              { key: "id", label: "ID", mono: true, width: 90,
                render: (t) => `T-${t.id.slice(-3)}` },
              { key: "title", label: "Task",
                render: (t) => (
                  <Link to={`/tasks/${t.id}`}
                        className="text-[color:var(--fg)] hover:text-[color:var(--cobalt)]">
                    {t.title}
                  </Link>
                ) },
              { key: "state", label: "State", width: 160,
                render: (t) => <Chip value={t.state} /> },
              { key: "priority", label: "Priority", width: 110,
                render: (t) => <Chip value={t.priority} /> },
              { key: "updated_at", label: "Updated", mono: true, width: 170, right: true,
                render: (t) => fmtDubai(t.updated_at) },
            ]}
            rows={tasks}
            empty="No tasks yet."
          />
        )}
      </Panel>

      {/* Ledger */}
      <Panel label="COST LEDGER" testId="project-ledger-panel"
             action={
               <span className="font-mono text-[10.5px] uppercase text-[color:var(--muted)]">
                 {entries.length} entries · lifetime
               </span>
             }>
        <DataTable
          testId="project-ledger-table"
          columns={[
            { key: "occurred_at", label: "Date", mono: true, width: 170,
              render: (e) => fmtDubai(e.occurred_at) },
            { key: "source", label: "Source", width: 140,
              render: (e) => <Chip value={e.source} /> },
            { key: "task", label: "Task", width: 110,
              render: (e) => e.task_id ? (
                <Link to={`/tasks/${e.task_id}`}
                      className="font-mono text-xs text-[color:var(--cobalt)] hover:brightness-125">
                  T-{String(e.task_id).slice(-3)}
                </Link>
              ) : <span className="text-[color:var(--muted)]">—</span> },
            { key: "ecu", label: "ECU", right: true, width: 110,
              render: (e) => e.ecu != null ? Number(e.ecu).toFixed(2) : "—" },
            { key: "usd", label: "USD", right: true, width: 110,
              render: (e) => (
                <span className={e.is_reversal ? "text-[color:var(--warn)]" : "text-[color:var(--fg)]"}>
                  {fmtUSD(e.usd)}
                </span>
              ) },
            { key: "method", label: "Method", width: 130,
              render: (e) => (
                <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-[color:var(--muted)]">
                  {e.method}
                </span>
              ) },
            { key: "note", label: "Note",
              render: (e) => (
                <span className="text-xs text-[color:var(--muted)] truncate inline-block max-w-[260px]"
                      title={e.note}>
                  {e.note || "—"}
                </span>
              ) },
            { key: "actions", label: "", right: true, width: 170,
              render: (e) => <ReverseCell entry={e} onReversed={refetchAll} /> },
          ]}
          rows={entries}
          empty="No cost entries yet."
        />
      </Panel>

      <EditProjectDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        project={project}
        onSaved={() => qc.invalidateQueries({ queryKey: ["project", slug] })}
      />
      <ProjectActionDialog
        open={actionOpen}
        onOpenChange={setActionOpen}
        project={project}
        action={action}
        canDelete={canDelete}
        deleteReason={deleteReason}
        onDone={() => {
          if (action === "delete") nav("/projects");
        }}
      />
    </div>
  );
}
